import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { isEqual } from 'lodash-es';
import { ScopedThemeProvider, useIsInModal, useOverlayContext, useStateInRef } from 'seagull';
import { isValidHexColor } from '~/business-logic/colorsLogic';
import { adjustBackgroundForContrast, getBrandColorWithContrast, getDisabledTextColorForBackground, getTextColorForBrandOnBackground, getTextColorsForBackground, getTextColorForTintedBrandBackground, getTextButtonOnSecondaryBgHover, validateAndCorrectGradient } from '~/business-logic/contrastLogic';
import { useStore, useStoreActions, useStoreStatic } from '~/contexts';
import { useUploadLogo } from '~/hooks/useUploadLogo';
import { useLocaleKeys } from '~/i18n';
import { handleRequest } from '~/infra/utils';
import { selectors } from '~/store/state-selectors';
import { makeDefaultBranding } from '~/store/store.utils';
const brandingToCSSVar = {
    backgroundGradientStart: '--bg-gradient-color-1',
    backgroundGradientEnd: '--bg-gradient-color-2',
    backgroundStandard: '--bg-standard',
    backgroundLight: '--bg-light',
    buttonPrimary: '--button-primary',
    buttonSecondary: '--button-secondary',
    buttonDisabled: '--button-disabled',
    progressBarComplete: '--stepper-completed',
    progressBarIncomplete: '--stepper-incomplete',
    textStandard: '--text-standard',
    textSecondary: '--text-secondary'
};
function getBrandCSS(branding, fallbackBranding) {
    var _ret_bggradientcolor1, _ret_bggradientcolor2, _ret_bgstandard, _ret_bglight, _ret_buttonprimary, _ret_bgstandard1, _ret_buttonprimary1, _ret_buttonsecondary, _ret_bgstandard2;
    const ret = {};
    Object.entries(brandingToCSSVar).forEach((param)=>{
        let [keyOfBranding, keyOfBrandCSS] = param;
        if (keyOfBranding === 'textStandard' || keyOfBranding === 'textSecondary') return;
        ret[keyOfBrandCSS] = !fallbackBranding || isValidHexColor(branding[keyOfBranding]) ? branding[keyOfBranding] : fallbackBranding[keyOfBranding];
    });
    const gradientStart = (_ret_bggradientcolor1 = ret['--bg-gradient-color-1']) !== null && _ret_bggradientcolor1 !== void 0 ? _ret_bggradientcolor1 : branding.backgroundGradientStart;
    const gradientEnd = (_ret_bggradientcolor2 = ret['--bg-gradient-color-2']) !== null && _ret_bggradientcolor2 !== void 0 ? _ret_bggradientcolor2 : branding.backgroundGradientEnd;
    const bgStandard = (_ret_bgstandard = ret['--bg-standard']) !== null && _ret_bgstandard !== void 0 ? _ret_bgstandard : branding.backgroundStandard;
    const bgLight = (_ret_bglight = ret['--bg-light']) !== null && _ret_bglight !== void 0 ? _ret_bglight : branding.backgroundLight;
    const gradientResult = validateAndCorrectGradient(gradientStart, gradientEnd);
    ret['--bg-gradient-color-1'] = gradientResult.startHex;
    ret['--bg-gradient-color-2'] = gradientResult.endHex;
    const primaryBgForText = gradientResult.startHex;
    const primaryTextColors = getTextColorsForBackground(primaryBgForText);
    ret['--text-on-primary-bg'] = primaryTextColors.primary;
    ret['--text-on-primary-bg-secondary'] = primaryTextColors.secondary;
    ret['--accent-on-primary-bg'] = getBrandColorWithContrast(primaryBgForText, (_ret_buttonprimary = ret['--button-primary']) !== null && _ret_buttonprimary !== void 0 ? _ret_buttonprimary : branding.buttonPrimary);
    if (isValidHexColor(bgLight)) {
        ret['--bg-light'] = adjustBackgroundForContrast(bgLight);
    }
    if (isValidHexColor(bgStandard)) {
        ret['--bg-standard'] = adjustBackgroundForContrast(bgStandard);
    }
    const correctedBgStandard = (_ret_bgstandard1 = ret['--bg-standard']) !== null && _ret_bgstandard1 !== void 0 ? _ret_bgstandard1 : isValidHexColor(bgStandard) ? bgStandard : null;
    const bgForText = correctedBgStandard && isValidHexColor(correctedBgStandard) ? correctedBgStandard : null;
    if (bgForText) {
        const textColors = getTextColorsForBackground(bgForText);
        ret['--text-standard'] = textColors.primary;
        ret['--text-secondary'] = textColors.secondary;
        ret['--text-on-standard-bg'] = textColors.primary;
        ret['--text-on-standard-bg-secondary'] = textColors.secondary;
        ret['--text-on-standard-bg-disabled'] = getDisabledTextColorForBackground(bgForText);
    }
    const buttonPrimary = (_ret_buttonprimary1 = ret['--button-primary']) !== null && _ret_buttonprimary1 !== void 0 ? _ret_buttonprimary1 : branding.buttonPrimary;
    ret['--button-text-on-filled'] = getTextColorsForBackground(buttonPrimary).primary;
    ret['--button-disabled'] = '#bfbfd0';
    ret['--button-disabled-dark'] = '#a9a9c1';
    ret['--text-on-tinted-brand-bg'] = getTextColorForTintedBrandBackground(buttonPrimary);
    ret['--badge-primary-bg'] = 'transparent';
    ret['--badge-primary-border'] = buttonPrimary;
    const buttonSecondary = (_ret_buttonsecondary = ret['--button-secondary']) !== null && _ret_buttonsecondary !== void 0 ? _ret_buttonsecondary : branding.buttonSecondary;
    if (isValidHexColor(buttonSecondary)) {
        ret['--button-text-on-outline-opaque'] = getTextColorForBrandOnBackground(buttonSecondary, buttonPrimary);
    }
    const correctedBgStandardForContrast = (_ret_bgstandard2 = ret['--bg-standard']) !== null && _ret_bgstandard2 !== void 0 ? _ret_bgstandard2 : isValidHexColor(bgStandard) ? bgStandard : null;
    if (correctedBgStandardForContrast) {
        const textButtonColor = getBrandColorWithContrast(correctedBgStandardForContrast, buttonPrimary);
        ret['--text-button-on-secondary-bg'] = textButtonColor;
        ret['--text-button-on-secondary-bg-hover'] = getTextButtonOnSecondaryBgHover(textButtonColor, buttonPrimary);
        ret['--step-selected-color'] = textButtonColor;
        ret['--badge-primary-color'] = textButtonColor;
    } else {
        ret['--badge-primary-color'] = ret['--text-on-tinted-brand-bg'];
    }
    return ret;
}
export function OverrideBranding(param) {
    let { children, className, testId } = param;
    const brandCSS = useMemo(()=>getBrandCSS(makeDefaultBranding()), []);
    return /*#__PURE__*/ _jsx(ScopedThemeProvider, {
        className: className,
        theme: brandCSS,
        testId: testId,
        children: children
    });
}
const CobBrandContext = /*#__PURE__*/ createContext({
    branding: makeDefaultBranding(),
    logoImage: '',
    isDirty: false,
    errors: new Set(),
    isValid: true,
    setBranding: ()=>{},
    revertToDefault: ()=>{},
    revertToInitial: ()=>{},
    saveLogoAndBranding: async ()=>{},
    setLogo: ()=>{}
});
export function CobBrandingProvider(props) {
    const getProps = useStateInRef(props);
    const { startLoader, stopLoader, showErrorToast, showSuccessToast } = useOverlayContext();
    const { t: commonTexts } = useLocaleKeys('common');
    const { t: errorTexts } = useLocaleKeys('errors');
    const businessBranding = useStore(selectors.user.branding);
    const worksAt = useStore(selectors.user.business);
    const { getState } = useStoreStatic();
    const { api, setBranding: setBusinessBranding, logBI } = useStoreActions();
    const { uploadLogo } = useUploadLogo(worksAt);
    const getUploadLogo = useStateInRef(uploadLogo);
    const [logo, setLogo] = useState();
    const businessLogoUrl = worksAt === null || worksAt === void 0 ? void 0 : worksAt.logoUrl;
    const initialBranding = useMemo(()=>props.initialBranding || businessBranding, [
        businessBranding,
        props.initialBranding
    ]);
    const [branding, setBranding] = useState(initialBranding);
    const value = useMemo(()=>{
        const logoUrl = logo === null || logo === void 0 ? void 0 : logo.asDataURL;
        const isBrandingDirty = !isEqual(branding, initialBranding);
        const isLogoDirty = !!logoUrl && logoUrl !== businessLogoUrl;
        const isDirty = isBrandingDirty || isLogoDirty;
        const errors = new Set(Object.entries(branding).filter((param)=>{
            let [, value] = param;
            return !isValidHexColor(value);
        }).map((param)=>{
            let [key] = param;
            return key;
        }));
        const isValid = errors.size === 0;
        return {
            branding,
            logoImage: logoUrl || businessLogoUrl || '',
            isDirty,
            errors,
            isValid,
            setLogo: (logo)=>{
                var _getProps_onLogoChange, _getProps;
                setLogo(logo);
                (_getProps_onLogoChange = (_getProps = getProps()).onLogoChange) === null || _getProps_onLogoChange === void 0 ? void 0 : _getProps_onLogoChange.call(_getProps, logo.asDataURL);
            },
            setBranding,
            revertToDefault: ()=>{
                logBI(11218);
                setBranding(makeDefaultBranding());
            },
            revertToInitial: ()=>{
                setBranding(initialBranding);
                setLogo(undefined);
            },
            saveLogoAndBranding: async ()=>{
                const businessId = selectors.user.businessId(getState());
                if (!isValid || !isDirty || !businessId) {
                    return;
                }
                logBI(11219);
                startLoader();
                const [updateBranding, { success: uploadLogoSuccess }] = await Promise.all([
                    isBrandingDirty ? handleRequest(api.user.updateBranding(businessId, branding)).then((result)=>{
                        if (!result.success) {
                            showErrorToast(errorTexts.unexpected(), 'update-branding-error');
                        } else {
                            setBusinessBranding(branding);
                        }
                        return result;
                    }) : handleRequest(Promise.resolve()),
                    isLogoDirty ? getUploadLogo()(logo) : Promise.resolve({
                        success: true
                    })
                ]);
                if (uploadLogoSuccess && updateBranding.success) {
                    showSuccessToast(commonTexts.toasts.savedSuccessfully(), 'update-branding-success');
                }
                stopLoader();
            }
        };
    }, [
        branding,
        initialBranding,
        logo,
        businessLogoUrl
    ]);
    const brandCSS = useMemo(()=>getBrandCSS(branding, initialBranding), [
        branding,
        initialBranding
    ]);
    const isInModal = useIsInModal();
    useEffect(()=>{
        if (isInModal) return;
        const buttonPrimary = brandCSS['--button-primary'];
        if (!buttonPrimary) return;
        document.body.style.setProperty('--button-primary', buttonPrimary);
        document.body.style.setProperty('--toast-info-bg', "color-mix(in srgb, ".concat(buttonPrimary, " 15%, white)"));
        document.body.style.setProperty('--toast-info-icon-color', buttonPrimary);
        return ()=>{
            document.body.style.removeProperty('--button-primary');
            document.body.style.removeProperty('--toast-info-bg');
            document.body.style.removeProperty('--toast-info-icon-color');
        };
    }, [
        brandCSS,
        isInModal
    ]);
    return /*#__PURE__*/ _jsx(CobBrandContext.Provider, {
        value: value,
        children: /*#__PURE__*/ _jsx(ScopedThemeProvider, {
            maxHeight: true,
            theme: brandCSS,
            children: props.children
        })
    });
}
export const useCobBranding = ()=>useContext(CobBrandContext);
